import { useNavigate } from 'react-router-dom'
import useRemoteSelectOptions from '../hooks/useRemoteSelectOptions'
import { Autocomplete, Box, CircularProgress } from '@mui/material'
import StyledTextField from './shared/StyledTextField'
import ListboxWithScrollPreservation from './ListboxWithScrollPreservation'
import { useEffect, useRef, useMemo, useCallback } from 'react'

const AutoCompleteFilter = (props) => {
  const { refetchDependencies = [], onChange, isMulti = false } = props

  const previousDepsRef = useRef(refetchDependencies)

  useEffect(() => {
    const previous = previousDepsRef.current
    const changed =
      JSON.stringify(previous) !== JSON.stringify(refetchDependencies)

    if (changed) {
      previousDepsRef.current = refetchDependencies
      onChange(isMulti ? [] : null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(refetchDependencies)])

  return <AutoCompleteFilterInner {...props} />
}

const AutoCompleteFilterInner = ({
  value,
  onChange,
  service,
  model,
  columnDataBase,
  placeholder,
  isMulti = false,
  label = '',
  extraFilters = {},
  valueKey = 'id',
  isClearable = true,
}) => {
  const navigate = useNavigate()

  const fieldKey = Array.isArray(columnDataBase)
    ? columnDataBase[0]
    : columnDataBase

  // Estabiliza extraFilters para evitar que o queryKey do React Query mude a cada render
  const stableExtraFilters = useMemo(
    () => extraFilters,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(extraFilters)]
  )

  // Memoiza o fetcher para evitar recriação da queryFn
  const fetcher = useCallback(
    async ({ page, perPage, search }) => {
      let searchFilter = {}
      if (search) {
        searchFilter = Array.isArray(columnDataBase)
          ? {
              $or: columnDataBase.map((col) => ({
                [col]: { $contains: search },
              })),
            }
          : { [columnDataBase]: search }
      }

      const restFilters = {
        deleted_at: { $eq: 'null' },
        ...stableExtraFilters,
      }

      const params = {
        filters: {
          ...restFilters,
          ...searchFilter,
        },
        page,
        perPage,
        sort: {
          column: 'id',
          order: 'asc',
          searchTerm: search,
        },
      }

      const response = await service.autocomplete(model, params, navigate)
      return response.result
    },
    [columnDataBase, stableExtraFilters, model, navigate, service]
  )

  // Memoiza mapToOption para evitar que o array de 'options' seja recriado do zero
  const mapToOption = useMemo(() => {
    return (item) => {
      let lbl = ''
      if (Array.isArray(columnDataBase)) {
        lbl = columnDataBase
          .map((col) => item[col])
          .filter(Boolean)
          .join(' - ')
      } else {
        lbl = item[columnDataBase] || item.name || item.label
      }

      const val = item[valueKey] || item.id
      return { label: lbl, value: val }
    }
  }, [columnDataBase, valueKey])

  const {
    options,
    selectedValue,
    isLoading,
    handleMenuScrollToBottom,
    handleSearch,
    isFetchingNextPage,
    activateQuery,
  } = useRemoteSelectOptions({
    queryKey: [model, columnDataBase],
    fetcher,
    mapToOption,
    fieldId: fieldKey,
    formData: { [fieldKey]: value },
    setFormData: (key, val) => {
      onChange(val)
    },
    isMulti: isMulti,
    initialPage: 1,
    perPage: 25,
    enabled: true,
    extraFilters: stableExtraFilters,
  })

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        multiple={isMulti}
        options={options}
        value={selectedValue || (isMulti ? [] : null)}
        onOpen={() => activateQuery()}
        loading={isLoading}
        disableClearable={!isClearable}
        disableListWrap={true}
        filterSelectedOptions={true}
        autoHighlight={false} // 👈 Evita destaque automático ao abrir
        getOptionKey={(option) => option.value} // 👈 Chave estável (MUI v5.14+)
        ListboxComponent={ListboxWithScrollPreservation}
        onInputChange={(event, newInputValue, reason) => {
          // Ignora o reset automático do MUI ao reabrir com valor selecionado
          if (reason === 'reset') return

          handleSearch(newInputValue)

          if (newInputValue === '' || reason === 'clear') {
            onChange(isMulti ? [] : null)
          }
        }}
        onChange={(event, newValue) => {
          if (isMulti) {
            onChange(Array.isArray(newValue) ? newValue : [])
          } else {
            onChange(newValue || null)
          }
        }}
        isOptionEqualToValue={(option, val) => {
          if (!val) return false
          return option.value === val.value
        }}
        clearOnBlur={false}
        ListboxProps={{
          onScroll: (event) => {
            // Não dispara nova busca se já estiver carregando
            if (isFetchingNextPage || isLoading) return

            const listboxNode = event.currentTarget
            const { scrollTop, clientHeight, scrollHeight } = listboxNode

            // Dispara a busca da próxima página quando atinge 90% do scroll
            const triggerThreshold = scrollHeight * 0.9

            if (scrollTop + clientHeight >= triggerThreshold) {
              handleMenuScrollToBottom()
            }
          },
        }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            size="small"
            label={label}
            placeholder={placeholder}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading || isFetchingNextPage ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  )
}

export default AutoCompleteFilter